import React from 'react';
import { Paper } from '@mui/material';

import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import _ from 'lodash';

// devexpress table components
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableColumnVisibility,
  DragDropProvider,
  TableColumnReordering,
  TableFixedColumns,
  Table,
} from '@devexpress/dx-react-grid-material-ui';
import { SortingState } from '@devexpress/dx-react-grid';

// native components, utils
import {
  DefaultTableHeaderCell,
  TableHeaderCellContent,
} from './table-components/headerComponents';
import { DefaultTableRow } from './table-components/rowComponents';
import { TableStateContext } from './TableStateContext';
import { isFillEnabled } from './fillEnabledTables';
import { useWindowResizeEffect } from './hooks/useWindowResizeEffect';
import {
  columnWidthEntriesOverflow,
  currentRootPx,
  fillColumnWidthEntries,
  mapColumnExtensions,
  rebaseColumnWidthEntries,
} from './table.utils';
import { mergeSavedColumnWidths, readColumnWidths } from './columnWidthStorage';
import {
  FixedColumnCell,
  DefaultTableCell,
  IdTableCell,
} from './table-components/cell-components/cell-components';
import { ColumnResizingPlugin } from './plugins/ColumnResizingPlugin';
import {
  RowDetailPlugin,
  ROW_DETAIL_TOGGLE_WIDTH,
} from './plugins/RowDetailPlugin';
import { SelectionPlugin } from './plugins/SelectionPlugin';

// Chrome columns added by plugins live outside the column config, so fill must
// reserve space for them (keyed to DevExtreme's default column widths) or the
// filled data columns overflow the container.
const CHROME_COLUMN_WIDTHS: Array<readonly [any, number]> = [
  [RowDetailPlugin, ROW_DETAIL_TOGGLE_WIDTH],
  [SelectionPlugin, 58], // TableSelection default selectionColumnWidth
];

export const getReservedChromeWidth = (children: any): number =>
  React.Children.toArray(children).reduce<number>((total, child: any) => {
    const match = CHROME_COLUMN_WIDTHS.find(([type]) => child?.type === type);
    if (!match) {
      return total;
    }
    // A listing that renders the chevron in its own Action cell collapses the toggle column, so
    // there is nothing to hold width back for.
    if (child?.props?.inlineToggle) {
      return total;
    }
    return total + match[1];
  }, 0);

// The widths to dispatch when restoring a previously dragged layout, or `null`
// when this table has no layout the user owns.
//
// Ownership turns on whether a saved layout *applies* here, not on whether its
// values differ. The ~37 listings that autosave `columnWidths` into uiSettings
// persist dragged values there too, so after a reload saved and current often
// match exactly; returning `null` then would leave `columnWidthsUserAdjusted`
// false and let fill overwrite the user's layout on the next container resize.
// Hence the current widths are returned unchanged when there is nothing to
// merge — the caller still dispatches, and that is what claims ownership.
export const getRestoredColumnWidths = (
  tableId: string | undefined,
  columnWidths: any[] | undefined
): any[] | null => {
  const saved = readColumnWidths(tableId);
  if (!saved.length || !columnWidths?.length) {
    return null;
  }

  // Ownership needs the saved layout to actually apply here — at least one
  // saved column must still exist. A saved set with no overlap at all (columns
  // renamed or replaced wholesale under a reused tableId) restores nothing, so
  // claiming ownership on it would switch fill off for that table permanently
  // and leave a gutter no user action could recover from.
  const currentNames = new Set(columnWidths.map((entry) => entry.columnName));
  if (!saved.some((entry) => currentNames.has(entry.columnName))) {
    return null;
  }

  return mergeSavedColumnWidths(saved, columnWidths) ?? columnWidths;
};

// Whether a listing already mounts the resizer itself. Fill tables that don't
// get one from BaseTable, rather than relying on every listing to remember it —
// leaving it to the listings is exactly how drag-to-resize went missing across
// the fill rollout (FIR-20964).
export const hasColumnResizingChild = (children: any): boolean =>
  React.Children.toArray(children).some(
    (child: any) => child?.type === ColumnResizingPlugin
  );

// Widths to hand the resizing plugin so a fill table renders full width, or
// `null` when nothing should change.
//
// Fill has to drive TableColumnResizing's state rather than `Table`'s
// columnExtensions: the plugin mounts after Table and overwrites those widths,
// which is why the fill rollout stripped the plugin instead (FIR-20964).
// Seeding the state the plugin reads is what lets a table be both filled and
// draggable. Returns `null` once the user has resized a column — their layout
// then owns the table — EXCEPT when the container itself has actually changed
// width (`containerWidthChanged`): a drag/restore must not block a real
// container resize from reflowing the table, or a wider container keeps a
// gutter and a narrower one keeps overflowing it forever (FIR-20968). Also
// returns `null` whenever the widths already fill, which is what keeps the
// caller's effect from looping (re-running against seeded widths yields no
// leftover space).
//
// Pure so it is unit-testable: BaseTable measures a real container, and jsdom
// reports every element as 0px wide.
// `rebase` re-derives the widths from the column configuration instead of
// growing the current ones — see rebaseColumnWidthEntries. The caller passes it
// when the set of hidden columns changed, which is the one case where growing
// the current widths gives a broken layout (FIR-20967). Widths that already
// overflow the container rebase whether or not the caller asked: fill can only
// hand out leftover space, so once the container has got narrower for any other
// reason — a vertical scrollbar appearing, a side panel opening, the window
// resized smaller — the seeded total stays where it was and the table keeps a
// horizontal scrollbar with nothing to scroll, across reloads, since the widths
// are persisted.
export const getSeededFillWidths = ({
  fillAvailableWidth,
  columnWidthsUserAdjusted,
  columnWidths,
  columns,
  containerWidth,
  reservedChromeWidth = 0,
  hiddenColumnNames,
  rebase = false,
  containerWidthChanged = false,
}: any): any[] | null => {
  if (
    !fillAvailableWidth ||
    (columnWidthsUserAdjusted && !containerWidthChanged) ||
    containerWidth <= 0 ||
    !columnWidths?.length
  ) {
    return null;
  }

  const available = Math.max(0, containerWidth - reservedChromeWidth);
  // Widths that already overflow have to be derived again for the same reason
  // a visibility change does: growing them cannot give width back.
  const overflows = columnWidthEntriesOverflow(
    columns,
    columnWidths,
    available,
    hiddenColumnNames
  );

  const fill =
    rebase || overflows ? rebaseColumnWidthEntries : fillColumnWidthEntries;
  const seeded = fill(columns, columnWidths, available, hiddenColumnNames);

  return _.isEqual(seeded, columnWidths) ? null : seeded;
};

// Measures an element's live content width via ResizeObserver and returns
// `[width, ref]`. The returned callback ref attaches the observer whenever the
// node actually mounts — including a late mount after a loading/placeholder
// render — and detaches on unmount, so fill still activates when the table
// Paper appears after the first render. Only active when `enabled` (fill tables).
export const useElementWidth = (enabled: boolean) => {
  const [width, setWidth] = React.useState(0);
  const observerRef = React.useRef<ResizeObserver | null>(null);

  const measureRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!enabled || !node) {
        return;
      }
      const update = () => setWidth(node.clientWidth);
      update();
      const observer = new ResizeObserver(update);
      observer.observe(node);
      observerRef.current = observer;
    },
    [enabled]
  );

  return [width, measureRef] as const;
};

// Whether `containerWidth` differs from the value this hook saw on its last call -- the signal
// that lets a container-size change reach a table the user has already resized (FIR-20968),
// while every other re-render for a different reason (a drag, a sort, a page change) reports
// `false`, which is what keeps `columnWidthsUserAdjusted` otherwise untouched by anything but
// the container itself moving.
//
// `useElementWidth` starts at `0` before its ResizeObserver callback ref ever attaches, so the
// first REAL measurement is a transition away from that placeholder, not a resize -- treating it
// as one let a restored user-adjusted layout get re-filled or rebased on every mount even though
// the container never moved. Only a change between two already-measured (non-zero) widths counts.
export const useContainerWidthChanged = (containerWidth: number): boolean => {
  const lastSeen = React.useRef(containerWidth);
  const previous = lastSeen.current;
  lastSeen.current = containerWidth;
  if (previous === 0 || containerWidth === 0) {
    return false;
  }
  return previous !== containerWidth;
};

export const BaseTable = ({ getRowId = (row: any) => row.id, ...props }) => {
  // @ts-expect-error TS(2339): Property 'state' does not exist on type 'unknown'.
  const { state, handlers, ...rest } = React.useContext(TableStateContext);
  return (
    <BaseTableBase
      {...props}
      {...state}
      {...handlers}
      {...rest}
      fillAvailableWidth={isFillEnabled(
        rest?.tableId,
        state?.fillAvailableWidth
      )}
      getRowId={getRowId}
    />
  );
};

// A table given a `height` lives in a bounded panel (the claim diary, a payee's invoices, a
// picker in a modal) and scrolls inside it, so its grid fills that height.
const BoundedRoot = (props: any) => (
  <Grid.Root {...props} style={{ height: '100%' }} />
);

// Every other table renders all its rows at their natural height and scrolls with the page,
// so a page has one vertical scrollbar, not one for the page and another for the table. The
// listings are paged, so the rows a page renders at once stay few.
export const tableLayoutFor = (
  height: string | number | undefined,
  isVirtualized: boolean
) => {
  const bounded = height !== undefined;
  return {
    root: bounded ? BoundedRoot : Grid.Root,
    virtualized: bounded && isVirtualized,
  };
};

// dev-express pins fixed-column cells with `position: sticky; z-index: 300`. Since tables scroll
// with the page (see tableLayoutFor), those cells travel up past the sticky PrimaryToolbar
// (z-index 100) and paint over it while the unpinned cells slide under it (FIR-21499).
// `isolation: isolate` keeps the cells' z-index inside the table, so the whole table passes
// under the toolbar as one layer. Menus and tooltips in table cells render in portals, so
// nothing inside a table needs to paint outside it.
export const tablePaperStyle = (
  height: string | number | undefined
): React.CSSProperties => ({ height, isolation: 'isolate' });

// Smart cell component that automatically detects ID columns
const SmartTableCell = (props: any) => {
  const columnName = props.column?.name;
  const isIdColumn =
    columnName?.toLowerCase().endsWith('id') ||
    columnName?.toLowerCase() === 'id';

  // Use IdTableCell for ID columns, DefaultTableCell for others
  if (isIdColumn) {
    return <IdTableCell {...props} />;
  }

  return <DefaultTableCell {...props} />;
};

const BaseTableBase = ({
  HeaderRowComponent,
  RowComponent,
  DataProcessors,
  Placeholder,
  height,
  isVirtualized = true,
  getRowId = (row: any) => row.id,
  dragEntityType,
  ...props
}: any) => {
  const qc = useQueryClient();
  const queryIsFetching = useIsFetching([props.tableId]);

  const ResolvedRowComponent = RowComponent || DefaultTableRow;

  const columns = React.useMemo(
    () =>
      Object.values(props.columns).map((column) => {
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        const columnName = column.name;
        const isIdColumn =
          columnName.toLowerCase().endsWith('id') ||
          columnName.toLowerCase() === 'id';

        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        return column.getCellValue
          ? {
              // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
              ...column,
              // @ts-expect-error TS(2571): Object is of type 'unknown'.
              title: column.title || ' ',
              getCellValue: (row: any) => {
                // @ts-expect-error TS(2571): Object is of type 'unknown'.
                const originalValue = column.getCellValue(row, props.context);
                return originalValue;
              },
            }
          : {
              // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
              ...column,
              // @ts-expect-error TS(2571): Object is of type 'unknown'.
              title: column.title || ' ',
              getCellValue: (row: any) => {
                const value = row[columnName] || '-';
                return value;
              },
            };
      }),
    [props.columns, props.context]
  );

  const sortState = React.useMemo(
    () =>
      props.orderBy && props.order
        ? [{ columnName: props.orderBy, direction: props.order }]
        : null,
    [props.order, props.orderBy]
  );

  // Non-fill tables keep the original window-driven width behaviour untouched.
  const defaultColumnExtensions = useWindowResizeEffect(
    (width: any) => mapColumnExtensions(columns, width),
    {
      delay: 500,
      id: props.tableId,
    }
  );

  // Fill tables distribute leftover space against the *measured* container width
  // (not window width) and recompute live when the container resizes or columns
  // are hidden. These extensions still carry the non-width column settings
  // (wrap/align/min) and act as the fill fallback; the widths that actually
  // render come from the resizing plugin, seeded below.
  const [containerWidth, measurePaperRef] = useElementWidth(
    !!props.fillAvailableWidth
  );
  const containerWidthChanged = useContainerWidthChanged(containerWidth);
  // Reserve space for plugin chrome columns (row-detail toggle, selection) that
  // live outside the column config, so filled data columns + chrome fit within
  // the container instead of overflowing into a horizontal scrollbar.
  const reservedChromeWidth = getReservedChromeWidth(props.children);
  const filledColumnExtensions = React.useMemo(
    () =>
      mapColumnExtensions(
        columns,
        Math.max(0, containerWidth - reservedChromeWidth),
        { fill: true, hiddenColumnNames: props.hiddenColumnNames }
      ),
    [columns, containerWidth, reservedChromeWidth, props.hiddenColumnNames]
  );

  const columnExtensions = props.fillAvailableWidth
    ? filledColumnExtensions
    : defaultColumnExtensions;

  const { columnWidths, onSeedColumnWidths, onColumnWidthsChange } = props;

  // Restore a layout the user previously dragged, once per table. Routed
  // through onColumnWidthsChange on purpose: that marks the widths user-owned,
  // so the fill pass below leaves them alone. Runs before the seeding effect
  // declared after it, so a restored table never flashes filled widths first.
  const restoredForTableId = React.useRef<string | undefined>(undefined);
  React.useLayoutEffect(() => {
    if (restoredForTableId.current === props.tableId || !onColumnWidthsChange) {
      return;
    }
    if (!columnWidths?.length) {
      return;
    }
    // Marked done whether or not anything was restored, so a table with no
    // saved layout stops re-reading localStorage on every subsequent render.
    restoredForTableId.current = props.tableId;
    const restored = getRestoredColumnWidths(props.tableId, columnWidths);
    if (restored) {
      onColumnWidthsChange(restored);
    }
    // `columnWidths` is read, not tracked beyond its length: re-running on
    // every width change would fight the user's next drag. The ref keeps this
    // to once per table; the length dep only covers widths arriving late.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tableId, onColumnWidthsChange, columnWidths?.length]);

  // Layout effect, not a plain effect: the resizing plugin renders from
  // columnWidths, so seeding after paint would show one frame at the unfilled
  // widths and then jump. useElementWidth's callback ref already measures
  // during commit, so seeding here lands the filled widths before the browser
  // paints — no flash on load.
  // A visibility change has to re-derive the widths from the column config
  // rather than grow the current ones (FIR-20967). Compared by reference, which
  // useTableState guarantees changes whenever the contents do.
  //
  // The root font is the other thing that has to re-derive rather than grow. Column widths are
  // rem, resolved to px once when they are seeded; the console re-bases the document from 12px
  // to 14 and 16 at 1620 and 1920, and a table already on screen when that happens kept the
  // pixels it was seeded with -- so a date column that was 10rem at 1440 was 7.5rem at 1980,
  // holding still while the text inside it grew by a third. Growing the current widths cannot
  // fix that, because they are already wrong; they have to be derived again.
  const seededHiddenNames = React.useRef(props.hiddenColumnNames);
  const seededRootPx = React.useRef(currentRootPx());
  React.useLayoutEffect(() => {
    const visibilityChanged =
      seededHiddenNames.current !== props.hiddenColumnNames;
    seededHiddenNames.current = props.hiddenColumnNames;

    const rootPx = currentRootPx();
    const rootChanged = seededRootPx.current !== rootPx;
    seededRootPx.current = rootPx;

    const seeded = getSeededFillWidths({
      fillAvailableWidth: props.fillAvailableWidth,
      columnWidthsUserAdjusted: props.columnWidthsUserAdjusted,
      columnWidths,
      columns,
      containerWidth,
      reservedChromeWidth,
      hiddenColumnNames: props.hiddenColumnNames,
      rebase: visibilityChanged || rootChanged,
      containerWidthChanged,
    });

    if (seeded && onSeedColumnWidths) {
      onSeedColumnWidths(seeded);
    }
  }, [
    props.fillAvailableWidth,
    props.columnWidthsUserAdjusted,
    props.hiddenColumnNames,
    columnWidths,
    onSeedColumnWidths,
    columns,
    containerWidth,
    reservedChromeWidth,
    containerWidthChanged,
  ]);

  const ownsColumnResizing = hasColumnResizingChild(props.children);

  // @ts-expect-error TS(2345): Argument of type '() => () => Promise<void>' is no... Remove this comment to see the full error message
  React.useEffect(() => {
    return () => qc.resetQueries([props.tableId]);
    // eslint-disable-next-line
  }, []);

  if (
    !queryIsFetching &&
    Placeholder &&
    Array.isArray(props.rows) &&
    _.isEmpty(props.rows)
  ) {
    return <Placeholder />;
  }

  const layout = tableLayoutFor(height, isVirtualized);
  const TableComponent = layout.virtualized ? VirtualTable : Table;

  return (
    <Paper
      ref={measurePaperRef}
      id={props.tableId}
      style={tablePaperStyle(height)}
      elevation={props.elevation ?? 6}
    >
      <Grid
        columns={columns}
        rows={props.rows || []}
        getRowId={getRowId}
        rootComponent={layout.root}
      >
        <SortingState
          sorting={sortState || []}
          onSortingChange={(newSort) => props.onSortingChange(newSort)}
          columnExtensions={props.sortingDisabledColumns}
        />

        {DataProcessors ? <DataProcessors /> : null}

        <TableComponent
          columnExtensions={columnExtensions}
          rowComponent={ResolvedRowComponent}
          cellComponent={SmartTableCell}
        />

        {props.children}

        {props.fillAvailableWidth && !ownsColumnResizing && (
          <ColumnResizingPlugin />
        )}

        <TableColumnVisibility
          hiddenColumnNames={props.hiddenColumnNames}
          onHiddenColumnNamesChange={(newHidden) =>
            props.onHiddenColumnNamesChange(newHidden)
          }
        />

        <DragDropProvider />
        <TableColumnReordering
          order={props.columnOrder}
          onOrderChange={(newOrder) => props.onOrderChange(newOrder)}
        />

        {HeaderRowComponent ? (
          <HeaderRowComponent />
        ) : (
          <TableHeaderRow
            showSortingControls={!!sortState}
            cellComponent={DefaultTableHeaderCell}
            contentComponent={(headerContentProps) => (
              <TableHeaderCellContent
                context={props.context}
                {...headerContentProps}
              />
            )}
          />
        )}
        <TableFixedColumns
          leftColumns={props.fixedLeft}
          rightColumns={props.fixedRight}
          cellComponent={FixedColumnCell}
        />
      </Grid>
    </Paper>
  );
};

export default BaseTableBase;
